/**
 * Shared image compression for module content. Keeps embedded base64 images under maxBytes
 * so the course update payload does not exceed server limits.
 */
const MAX_EMBED_IMAGE_BYTES = 400 * 1024; // 400KB
const MAX_INPUT_IMAGE_BYTES = 20 * 1024 * 1024; // 20MB (original image before compression)

function estimateBytesFromDataUrl(dataUrl) {
  if (typeof dataUrl !== 'string') return 0;
  const comma = dataUrl.indexOf(',');
  if (comma === -1) return 0;
  const base64 = dataUrl.slice(comma + 1);
  const len = base64.length;
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((len * 3) / 4) - padding;
}

function compressImage(dataUrl, maxWidth, maxHeight, quality) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

function compressDataUrlToMaxBytes(dataUrl, maxBytes) {
  const dimensionSteps = [
    { w: 1280, h: 720 },
    { w: 1024, h: 576 },
    { w: 800, h: 450 },
    { w: 640, h: 360 },
    { w: 480, h: 270 },
    { w: 400, h: 225 },
    { w: 320, h: 180 },
  ];
  const qualitySteps = [0.7, 0.55, 0.45, 0.35, 0.28, 0.22, 0.18, 0.14, 0.1];
  const limit = maxBytes || MAX_EMBED_IMAGE_BYTES;

  const tryStep = (dimIdx, qIdx) => {
    if (dimIdx >= dimensionSteps.length) {
      return Promise.reject(new Error('Image is too large (max 400KB).'));
    }
    const dim = dimensionSteps[dimIdx];
    const quality = qualitySteps[Math.min(qIdx, qualitySteps.length - 1)];
    return compressImage(dataUrl, dim.w, dim.h, quality).then(result => {
      const bytes = estimateBytesFromDataUrl(result);
      if (bytes > 0 && bytes <= limit) return result;
      if (qIdx + 1 < qualitySteps.length) return tryStep(dimIdx, qIdx + 1);
      return tryStep(dimIdx + 1, 0);
    });
  };
  return tryStep(0, 0);
}

/**
 * Compress a File or data URL string to under maxBytes. For use in editor paste/upload.
 */
export function compressImageToMaxBytes(input, maxBytes) {
  const limit = maxBytes || MAX_EMBED_IMAGE_BYTES;
  if (input instanceof File) {
    if (typeof input.size === 'number' && input.size > MAX_INPUT_IMAGE_BYTES) {
      return Promise.reject(
        new Error('Image is too large (max 20MB). Please use a smaller image.'),
      );
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        const dataUrl = e.target.result;
        if (typeof dataUrl === 'string' && dataUrl.startsWith('data:image')) {
          compressDataUrlToMaxBytes(dataUrl, limit).then(resolve).catch(reject);
        } else {
          reject(new Error('Invalid image file'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(input);
    });
  }
  if (typeof input === 'string' && input.startsWith('data:image')) {
    return compressDataUrlToMaxBytes(input, limit);
  }
  return Promise.reject(new Error('Invalid input'));
}

/**
 * Finds data:image/...;base64,... in html and compresses any that exceed maxBytes.
 * Returns a Promise of the modified html.
 */
export function compressOversizedImagesInHtml(html, maxBytes) {
  if (typeof html !== 'string') return Promise.resolve(html);
  const regex = /(data:image\/[^;]+;base64,[A-Za-z0-9+/=]+)/g;
  const matches = [];
  let match;
  while ((match = regex.exec(html)) !== null) {
    matches.push({ full: match[1], index: match.index });
  }
  if (matches.length === 0) return Promise.resolve(html);
  const limit = maxBytes || MAX_EMBED_IMAGE_BYTES;
  let result = html;
  return matches.reduce((promise, { full, index }) => {
    return promise.then(() => {
      if (estimateBytesFromDataUrl(full) <= limit) return result;
      return compressDataUrlToMaxBytes(full, limit).then(compressed => {
        result = result.split(full).join(compressed);
        return result;
      }).catch(() => result);
    });
  }, Promise.resolve(result));
}

export {
  MAX_EMBED_IMAGE_BYTES,
  MAX_INPUT_IMAGE_BYTES,
  estimateBytesFromDataUrl,
  compressImage,
  compressDataUrlToMaxBytes,
};
